import GardienVoicesCard from "@/components/admin/GardienVoicesCard";
import VoiceQuotaCard from "@/components/admin/VoiceQuotaCard";
import GroupGamesAdmin from "@/components/admin/GroupGamesAdmin";
import MusicAdmin from "@/components/admin/MusicAdmin";
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOGO } from "@/data/exercises";
import { FreeAccessManager } from "@/components/admin/FreeAccessManager";
import { SubcategoryManager } from "@/components/admin/SubcategoryManager";
import { StoryManager } from "@/components/admin/StoryManager";
import { VoiceSelector } from "@/components/admin/VoiceSelector";
import { ProductClicks } from "@/components/admin/ProductClicks";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft, Plus, Pencil, Trash2, ImagePlus, Loader2, Gamepad2, ScrollText,
  Brain, Puzzle, Music, Music2, X, Check, Lightbulb, Link2, Clock, Tag, KeyRound, Volume2,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CATS = {
  quiz: { label: "Quiz", icon: Brain, color: "#2563EB" },
  comprendre: { label: "Mieux Comprendre", icon: Lightbulb, color: "#2E8EC2" },
  puzzle: { label: "Puzzle", icon: Puzzle, color: "#7C3AED" },
  blindtest: { label: "Blind Test", icon: Music, color: "#DB2777" },
  pronunciation: { label: "Prononciation", icon: Volume2, color: "#0F766E" },
  breathing: { label: "Respiration", icon: Volume2, color: "#0EA5A0" },
  colors: { label: "Couleurs", icon: Volume2, color: "#8B5CF6" },
  sounds: { label: "Sons doux", icon: Music, color: "#DB2777" },
};
const DECADES = ["60", "70", "80"];
const PRESET_THEME = "Douceur d'Été & Souvenirs de Vacances";
const MAIN_CATS = [
  { id: "alzheimer", label: "Te souviens-tu ?" },
  { id: "autisme", label: "Mon petit monde" },
  { id: "aphasie", label: "Dis-moi" },
  { id: "sensoriels", label: "Éveil des sens" },
  { id: "routine", label: "Au fil du jour" },
];
const EMPTY = {
  title: "", category: "quiz", main_category: "", subcategory_id: "", theme: "", description: "", rules: "", image_url: "",
  audio_url: "", media_url: "", start_seconds: 0, hint: "", decade: "60", answer_title: "", answer_artist: "",
  questions: [], words: [], product_name: "", product_desc: "", product_image: "", product_url: "",
  products: [], grid: 3, active: true, printable: false, order: 0,
};
const PRINTABLE = ["quiz", "comprendre", "puzzle", "pronunciation"];

export default function AdminPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [pwdSaving, setPwdSaving] = useState(false);

  const changePwd = async () => {
    if (pwd.next.length < 6) return toast.error("Nouveau mot de passe : 6 caractères minimum.");
    if (pwd.next !== pwd.confirm) return toast.error("Les mots de passe ne correspondent pas.");
    setPwdSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pwd.current, new_password: pwd.next });
      toast.success("Mot de passe mis à jour");
      setPwdOpen(false);
      setPwd({ current: "", next: "", confirm: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec du changement de mot de passe.");
    } finally {
      setPwdSaving(false);
    }
  };
  const imgRef = useRef(null);
  const audioRef = useRef(null);
  const productImgRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/games");
      setGames(data);
    } catch {
      toast.error("Chargement impossible.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const { data } = await api.get("/game-categories");
      setCategories(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (user && user.role !== "admin") navigate("/tableau-de-bord");
  }, [user, navigate]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadCategories(); }, [loadCategories]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (g) => {
    setEditing(g);
    setForm({ ...EMPTY, ...g, questions: g.questions || [], words: g.words || [],
      products: (g.products && g.products.length) ? g.products
        : (g.product_url ? [{ name: g.product_name || "", desc: g.product_desc || "", image: g.product_image || "", url: g.product_url }] : []) });
    setOpen(true);
  };

  const upload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, [field]: `${BACKEND}${data.url}` }));
      toast.success("Fichier envoyé");
    } catch {
      toast.error("Échec de l'envoi du fichier.");
    } finally {
      setUploading(false);
    }
  };

  // Quiz question helpers
  const addQuestion = () =>
    setForm((f) => ({ ...f, questions: [...f.questions, { question: "", options: ["", "", "", ""], answer: 0, tip: "" }] }));
  const updateQuestion = (i, patch) =>
    setForm((f) => ({ ...f, questions: f.questions.map((q, k) => (k === i ? { ...q, ...patch } : q)) }));
  const updateOption = (i, oi, val) =>
    setForm((f) => ({
      ...f,
      questions: f.questions.map((q, k) =>
        k === i ? { ...q, options: q.options.map((o, j) => (j === oi ? val : o)) } : q),
    }));
  const removeQuestion = (i) =>
    setForm((f) => ({ ...f, questions: f.questions.filter((_, k) => k !== i) }));

  // Pronunciation word helpers
  const addWord = () =>
    setForm((f) => ({ ...f, words: [...f.words, { text: "", image_url: "", audio_url: "" }] }));
  const updateWord = (i, patch) =>
    setForm((f) => ({ ...f, words: f.words.map((w, k) => (k === i ? { ...w, ...patch } : w)) }));
  const removeWord = (i) =>
    setForm((f) => ({ ...f, words: f.words.filter((_, k) => k !== i) }));

  // Objets recommandés (jusqu'à 3)
  const addProduct = () =>
    setForm((f) => ({ ...f, products: [...(f.products || []), { name: "", desc: "", image: "", url: "" }] }));
  const updateProduct = (i, patch) =>
    setForm((f) => ({ ...f, products: f.products.map((p, k) => (k === i ? { ...p, ...patch } : p)) }));
  const removeProduct = (i) =>
    setForm((f) => ({ ...f, products: f.products.filter((_, k) => k !== i) }));
  const uploadProductImage = async (e, i) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      updateProduct(i, { image: `${BACKEND}${data.url}` });
      toast.success("Image envoyée");
    } catch { toast.error("Échec de l'envoi."); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Le titre est obligatoire.");
    if (!form.main_category) return toast.error("Choisissez une catégorie principale.");
    if (!form.subcategory_id) return toast.error("Choisissez une sous-catégorie.");
    if ((form.category === "quiz" || form.category === "comprendre") && form.questions.length === 0)
      return toast.error("Ajoutez au moins une question.");
    if (form.category === "blindtest" && !form.audio_url && !form.media_url)
      return toast.error("Ajoutez un extrait audio ou collez un lien.");
    if (form.category === "puzzle" && !form.image_url)
      return toast.error("Ajoutez une image pour le puzzle.");
    if (form.category === "pronunciation" && form.words.filter((w) => w.text.trim()).length === 0)
      return toast.error("Ajoutez au moins un mot à prononcer.");
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/games/${editing.id}`, form);
        toast.success("Jeu mis à jour");
      } else {
        await api.post("/admin/games", form);
        toast.success("Jeu ajouté");
      }
      setOpen(false);
      load();
    } catch {
      toast.error("Enregistrement impossible.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (g) => {
    try {
      await api.put(`/admin/games/${g.id}`, { ...g, active: !g.active });
      setGames((gs) => gs.map((x) => (x.id === g.id ? { ...x, active: !x.active } : x)));
    } catch {
      toast.error("Modification impossible.");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/games/${id}`);
      toast.success("Jeu supprimé");
      load();
    } catch {
      toast.error("Suppression impossible.");
    }
  };

  const activeCount = games.filter((g) => g.active).length;
  const [gameFilter, setGameFilter] = useState("all");
  const FILTERS = [["all", "Tous", () => true], ["review", "À relire (Gemini)", (g) => g.generated_by === "gemini" && !g.active],
    ["active", "Actifs", (g) => g.active], ["hidden", "Masqués", (g) => !g.active]];
  const shownGames = games.filter(FILTERS.find((f) => f[0] === gameFilter)[2]);

  return (
    <div className="min-h-screen bg-[#F4F0E8] font-care">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="Passerelle" className="h-10 w-auto" />
            <span className="text-gray-300">·</span>
            <span className="text-gray-500 text-sm font-semibold">Administration</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPwdOpen(true)} className="rounded-lg" data-testid="change-password-button">
              <KeyRound className="w-4 h-4 mr-1" /> Mot de passe
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate("/tableau-de-bord")} data-testid="admin-back-button">
              <ArrowLeft className="w-4 h-4 mr-1" /> Tableau de bord
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="font-serif-display text-3xl font-normal text-gray-800">Gestion des jeux</h1>
            <p className="text-gray-500 mt-1">Quiz, Puzzles et Blind Test — activez ceux à proposer cette semaine.</p>
          </div>
          <Button onClick={openNew} className="bg-emerald-700 hover:bg-emerald-800 rounded-xl" data-testid="add-game-button">
            <Plus className="w-4 h-4 mr-1" /> Nouveau jeu
          </Button>
        </div>
        <p className="text-sm text-emerald-700 font-semibold mb-8" data-testid="active-count">
          {activeCount} jeu(x) actif(s) sur la page principale
        </p>

        <FreeAccessManager />

        <SubcategoryManager onChanged={loadCategories} />

        <StoryManager />

        <VoiceSelector />
        <GardienVoicesCard />
        <VoiceQuotaCard />
        <GroupGamesAdmin />
        <MusicAdmin />

        <ProductClicks />

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-emerald-700" /></div>
        ) : games.length === 0 ? (
          <Card className="border-dashed border-2 border-gray-300 bg-transparent">
            <CardContent className="p-14 flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-2xl bg-emerald-50 flex items-center justify-center mb-4">
                <Gamepad2 className="w-8 h-8 text-emerald-700" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Aucun jeu pour l'instant</h3>
              <p className="text-gray-500 max-w-sm">Ajoutez un Quiz, un Puzzle ou un Blind Test musical.</p>
            </CardContent>
          </Card>
        ) : (
          <>
          <div className="flex flex-wrap gap-2 mb-5" data-testid="admin-games-filters">
            {FILTERS.map(([id, label, fn]) => (
              <button key={id} onClick={() => setGameFilter(id)} data-testid={`admin-games-filter-${id}`}
                className={`h-9 px-4 rounded-full text-sm font-semibold border transition-colors ${gameFilter === id ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-gray-700 border-gray-200 hover:border-emerald-300"}`}>
                {label} · {games.filter(fn).length}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="admin-games-grid">
            {shownGames.map((g) => {
              const cat = CATS[g.category] || CATS.quiz;
              const CatIcon = cat.icon;
              return (
                <Card key={g.id} className="border-gray-200 overflow-hidden" data-testid={`admin-game-${g.id}`}>
                  <div className="h-36 bg-gray-100 relative">
                    {g.image_url ? (
                      <img src={g.image_url} alt={g.title} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center" style={{ color: cat.color }}>
                        <CatIcon className="w-12 h-12 opacity-40" />
                      </div>
                    )}
                    <span className="absolute top-3 left-3 text-xs font-bold px-3 py-1 rounded-full text-white flex items-center gap-1"
                      style={{ backgroundColor: cat.color }}>
                      <CatIcon className="w-3 h-3" /> {cat.label}
                    </span>
                    <span className={`absolute top-3 right-3 text-xs font-bold px-3 py-1 rounded-full ${
                      g.active ? "bg-emerald-600 text-white" : "bg-gray-300 text-gray-700"}`}>
                      {g.active ? "Actif" : "Masqué"}
                    </span>
                    {g.generated_by === "gemini" && (
                      <span className="absolute bottom-3 left-3 text-xs font-bold px-3 py-1 rounded-full bg-white text-amber-800 shadow-sm" data-testid={`gemini-badge-${g.id}`}>Créé par Gemini</span>
                    )}
                    {g.printable && PRINTABLE.includes(g.category) && (
                      <span className="absolute bottom-3 right-3 text-xs font-bold px-3 py-1 rounded-full bg-white text-emerald-800 shadow-sm" data-testid={`printable-badge-${g.id}`}>Imprimable</span>
                    )}
                  </div>
                  <CardContent className="p-5">
                    <h3 className="font-bold text-lg text-gray-800 mb-1">{g.title}</h3>
                    {g.theme && <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5 mb-2"><Tag className="w-3 h-3" /> {g.theme}</span>}
                    <p className="text-sm text-gray-500 line-clamp-2 mb-4 min-h-[2.5rem]">
                      {(g.category === "quiz" || g.category === "comprendre") ? `${(g.questions || []).length} question(s)` :
                       g.category === "blindtest" ? `Années ${g.decade} — ${g.answer_title || "?"}` :
                       g.description || "Puzzle image"}
                    </p>
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Switch checked={g.active} onCheckedChange={() => toggleActive(g)} data-testid={`toggle-game-${g.id}`} />
                        <span className="text-sm text-gray-600">Cette semaine</span>
                      </label>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" className="rounded-lg" onClick={() => openEdit(g)} data-testid={`edit-game-${g.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm" className="rounded-lg text-red-600" data-testid={`delete-game-${g.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Supprimer « {g.title} » ?</AlertDialogTitle>
                              <AlertDialogDescription>Cette action est définitive.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annuler</AlertDialogCancel>
                              <AlertDialogAction onClick={() => remove(g.id)} className="bg-red-600 hover:bg-red-700" data-testid={`confirm-delete-game-${g.id}`}>
                                Supprimer
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          </>
        )}
      </main>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="game-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifier le jeu" : "Nouveau jeu"}</DialogTitle>
            <DialogDescription>Choisissez la catégorie puis renseignez les informations.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Catégorie</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1" data-testid="game-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="quiz">Quiz</SelectItem>
                  <SelectItem value="comprendre">Mieux Comprendre</SelectItem>
                  <SelectItem value="puzzle">Puzzle</SelectItem>
                  <SelectItem value="blindtest">Blind Test Musical</SelectItem>
                  <SelectItem value="pronunciation">Prononciation assistée</SelectItem>
                  <SelectItem value="breathing">Respiration guidée (sensoriel)</SelectItem>
                  <SelectItem value="colors">Couleurs apaisantes (sensoriel)</SelectItem>
                  <SelectItem value="sounds">Sons doux (sensoriel)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Titre</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Nom du jeu" className="mt-1" data-testid="game-title-input" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Catégorie principale</Label>
                <Select value={form.main_category || ""} onValueChange={(v) => setForm({ ...form, main_category: v, subcategory_id: "" })}>
                  <SelectTrigger className="mt-1" data-testid="game-maincat-select"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                  <SelectContent>
                    {MAIN_CATS.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Sous-catégorie</Label>
                <Select value={form.subcategory_id || ""} onValueChange={(v) => setForm({ ...form, subcategory_id: v })} disabled={!form.main_category}>
                  <SelectTrigger className="mt-1" data-testid="game-subcat-select"><SelectValue placeholder={form.main_category ? "Choisir…" : "Choisir une catégorie d'abord"} /></SelectTrigger>
                  <SelectContent>
                    {(categories.find((c) => c.id === form.main_category)?.subcategories || []).map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="flex items-center gap-1"><Tag className="w-4 h-4" /> Thème</Label>
              <Input value={form.theme} onChange={(e) => setForm({ ...form, theme: e.target.value })}
                placeholder="Ex : Douceur d'Été & Souvenirs de Vacances" className="mt-1" data-testid="game-theme-input" />
              <button type="button" onClick={() => setForm({ ...form, theme: PRESET_THEME })}
                className="mt-2 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1"
                data-testid="preset-theme-button">
                + {PRESET_THEME}
              </button>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Courte présentation" className="mt-1" data-testid="game-description-input" />
            </div>
            <div>
              <Label className="flex items-center gap-1"><ScrollText className="w-4 h-4" /> Règles</Label>
              <Textarea value={form.rules} onChange={(e) => setForm({ ...form, rules: e.target.value })}
                placeholder="Comment jouer" className="mt-1" data-testid="game-rules-input" />
            </div>

            {(form.category === "quiz" || form.category === "comprendre") && (
              <div className="border-t pt-4">
                {form.category === "comprendre" && (
                  <div className="rounded-xl bg-teal-50 border border-teal-200 p-3 mb-3 text-sm text-teal-800 flex gap-2">
                    <Lightbulb className="w-5 h-5 shrink-0" />
                    <span>Alternez des questions sur les passions du patient et des informations bienveillantes sur les pathologies. Ajoutez une « bulle de conseil » à chaque question.</span>
                  </div>
                )}
                <div className="flex items-center justify-between mb-3">
                  <Label className="flex items-center gap-1"><Brain className="w-4 h-4" /> Questions</Label>
                  <Button type="button" variant="outline" size="sm" className="rounded-lg" onClick={addQuestion} data-testid="add-question-button">
                    <Plus className="w-4 h-4 mr-1" /> Question
                  </Button>
                </div>
                {form.questions.length === 0 && <p className="text-sm text-gray-400">Aucune question. Ajoutez-en une.</p>}
                <div className="space-y-4">
                  {form.questions.map((q, i) => (
                    <div key={i} className="rounded-xl border border-gray-200 p-3 bg-gray-50" data-testid={`question-block-${i}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-gray-600">Question {i + 1}</span>
                        <button onClick={() => removeQuestion(i)} className="text-red-500" data-testid={`remove-question-${i}`}>
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <Input value={q.question} onChange={(e) => updateQuestion(i, { question: e.target.value })}
                        placeholder="Intitulé de la question" className="mb-2 bg-white" data-testid={`question-input-${i}`} />
                      <p className="text-xs text-gray-500 mb-1">Cochez la bonne réponse :</p>
                      {q.options.map((opt, oi) => (
                        <div key={oi} className="flex items-center gap-2 mb-1.5">
                          <button type="button" onClick={() => updateQuestion(i, { answer: oi })}
                            data-testid={`answer-radio-${i}-${oi}`}
                            className={`w-7 h-7 shrink-0 rounded-full border-2 flex items-center justify-center ${
                              q.answer === oi ? "bg-emerald-600 border-emerald-600 text-white" : "border-gray-300 bg-white"}`}>
                            {q.answer === oi && <Check className="w-4 h-4" />}
                          </button>
                          <Input value={opt} onChange={(e) => updateOption(i, oi, e.target.value)}
                            placeholder={`Réponse ${oi + 1}`} className="bg-white" data-testid={`option-input-${i}-${oi}`} />
                        </div>
                      ))}
                      <div className="mt-2">
                        <Label className="flex items-center gap-1 text-xs text-teal-700"><Lightbulb className="w-3.5 h-3.5" /> Bulle de conseil (affichée après la réponse)</Label>
                        <Textarea value={q.tip || ""} onChange={(e) => updateQuestion(i, { tip: e.target.value })}
                          placeholder="Explication simple et rassurante du sujet" className="mt-1 bg-white text-sm" data-testid={`question-tip-${i}`} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {form.category === "pronunciation" && (
              <div className="border-t pt-4 space-y-3" data-testid="pronunciation-editor">
                <div className="rounded-xl bg-teal-50 border border-teal-200 p-3 text-sm text-teal-800">
                  Saisissez les mots à prononcer. La <b>lecture audio est générée automatiquement</b> (voix douce) à l'enregistrement. L'image est optionnelle.
                </div>
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-1"><Volume2 className="w-4 h-4" /> Mots à prononcer</Label>
                  <Button type="button" variant="outline" size="sm" className="rounded-lg" onClick={addWord} data-testid="add-word-button">
                    <Plus className="w-4 h-4 mr-1" /> Ajouter un mot
                  </Button>
                </div>
                {form.words.length === 0 && <p className="text-sm text-gray-400">Aucun mot. Ajoutez-en un.</p>}
                {form.words.map((w, i) => (
                  <div key={i} className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-xl p-3" data-testid={`word-row-${i}`}>
                    <div className="w-14 h-14 rounded-lg bg-white overflow-hidden flex items-center justify-center border shrink-0">
                      {w.image_url ? <img src={w.image_url} alt="" className="w-full h-full object-cover" /> : <ImagePlus className="w-5 h-5 text-gray-300" />}
                    </div>
                    <Input value={w.text} onChange={(e) => updateWord(i, { text: e.target.value })}
                      placeholder="Mot (ex. Bonjour)" className="flex-1" data-testid={`word-text-${i}`} />
                    <input type="file" accept="image/*" hidden id={`word-img-${i}`}
                      onChange={async (e) => {
                        const file = e.target.files?.[0]; if (!file) return;
                        const fd = new FormData(); fd.append("file", file);
                        try { const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); updateWord(i, { image_url: `${BACKEND}${data.url}` }); toast.success("Image envoyée"); }
                        catch { toast.error("Échec de l'envoi."); }
                      }} data-testid={`word-image-input-${i}`} />
                    <Button type="button" variant="outline" size="sm" className="rounded-lg" onClick={() => document.getElementById(`word-img-${i}`).click()} data-testid={`word-upload-${i}`}>
                      <ImagePlus className="w-4 h-4" />
                    </Button>
                    <button type="button" onClick={() => removeWord(i)} className="text-red-500 hover:text-red-700" data-testid={`remove-word-${i}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}

            {form.category === "puzzle" && (
              <div className="border-t pt-4 space-y-4">
                <div>
                  <Label>Image du puzzle</Label>
                  <div className="mt-1 flex items-center gap-4">
                    <div className="w-24 h-24 rounded-xl bg-gray-100 overflow-hidden flex items-center justify-center border">
                      {form.image_url ? <img src={form.image_url} alt="aperçu" className="w-full h-full object-cover" /> : <ImagePlus className="w-7 h-7 text-gray-300" />}
                    </div>
                    <input ref={imgRef} type="file" accept="image/*" hidden onChange={(e) => upload(e, "image_url")} data-testid="game-image-input" />
                    <Button type="button" variant="outline" className="rounded-lg" disabled={uploading} onClick={() => imgRef.current?.click()} data-testid="game-upload-image">
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ImagePlus className="w-4 h-4 mr-1" /> Choisir une image</>}
                    </Button>
                  </div>
                </div>
                <div>
                  <Label>Difficulté (taille de la grille)</Label>
                  <Select value={String(form.grid)} onValueChange={(v) => setForm({ ...form, grid: Number(v) })}>
                    <SelectTrigger className="mt-1" data-testid="game-grid-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="2">Facile (2 × 2)</SelectItem>
                      <SelectItem value="3">Moyen (3 × 3)</SelectItem>
                      <SelectItem value="4">Difficile (4 × 4)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {form.category === "blindtest" && (
              <div className="border-t pt-4 space-y-4">
                <div>
                  <Label className="flex items-center gap-1"><Music2 className="w-4 h-4" /> Extrait audio</Label>
                  <div className="mt-1 flex items-center gap-4">
                    {form.audio_url ? (
                      <audio src={form.audio_url} controls className="h-10" data-testid="admin-audio-preview" />
                    ) : (
                      <span className="text-sm text-gray-400">Aucun extrait</span>
                    )}
                    <input ref={audioRef} type="file" accept="audio/*" hidden onChange={(e) => upload(e, "audio_url")} data-testid="game-audio-input" />
                    <Button type="button" variant="outline" className="rounded-lg" disabled={uploading} onClick={() => audioRef.current?.click()} data-testid="game-upload-audio">
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Music2 className="w-4 h-4 mr-1" /> Choisir un extrait</>}
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="flex items-center gap-1"><Link2 className="w-4 h-4" /> Ou coller un lien (YouTube ou audio)</Label>
                  <Input value={form.media_url} onChange={(e) => setForm({ ...form, media_url: e.target.value })}
                    placeholder="https://www.youtube.com/watch?v=..." className="mt-1" data-testid="game-media-url" />
                </div>
                <div>
                  <Label className="flex items-center gap-1"><Clock className="w-4 h-4" /> Début de l'extrait (secondes)</Label>
                  <Input type="number" min={0} value={form.start_seconds}
                    onChange={(e) => setForm({ ...form, start_seconds: Number(e.target.value) || 0 })}
                    placeholder="0" className="mt-1 w-32" data-testid="game-start-seconds" />
                </div>
                <div>
                  <Label>Décennie</Label>
                  <Select value={form.decade} onValueChange={(v) => setForm({ ...form, decade: v })}>
                    <SelectTrigger className="mt-1" data-testid="game-decade-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DECADES.map((d) => <SelectItem key={d} value={d}>Années {d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Titre de la chanson</Label>
                    <Input value={form.answer_title} onChange={(e) => setForm({ ...form, answer_title: e.target.value })}
                      placeholder="Ex : Ne me quitte pas" className="mt-1" data-testid="game-answer-title" />
                  </div>
                  <div>
                    <Label>Artiste</Label>
                    <Input value={form.answer_artist} onChange={(e) => setForm({ ...form, answer_artist: e.target.value })}
                      placeholder="Ex : Jacques Brel" className="mt-1" data-testid="game-answer-artist" />
                  </div>
                </div>
                <div>
                  <Label className="flex items-center gap-1"><Lightbulb className="w-4 h-4" /> Indice (année, parole célèbre…)</Label>
                  <Input value={form.hint} onChange={(e) => setForm({ ...form, hint: e.target.value })}
                    placeholder="Ex : Sortie en 1975 — « ...paroles célèbres... »" className="mt-1" data-testid="game-hint-input" />
                </div>
              </div>
            )}

            <div className="border-t pt-4 space-y-3" data-testid="product-editor">
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800 flex items-center justify-between gap-2">
                <span><b>Objets recommandés (objetsdouceur.com)</b> — jusqu'à 3, optionnels. Affichés sur la fiche du jeu.</span>
                <Button type="button" variant="outline" size="sm" className="rounded-lg shrink-0" disabled={(form.products || []).length >= 3}
                  onClick={addProduct} data-testid="add-product-button">
                  <Plus className="w-4 h-4 mr-1" /> Ajouter
                </Button>
              </div>
              {(form.products || []).length === 0 && <p className="text-sm text-gray-400">Aucun objet recommandé.</p>}
              {(form.products || []).map((p, i) => (
                <div key={i} className="rounded-xl border border-gray-200 p-3 space-y-2" data-testid={`product-row-${i}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 rounded-lg bg-white overflow-hidden flex items-center justify-center border shrink-0">
                      {p.image ? <img src={p.image} alt="" className="w-full h-full object-cover" /> : <ImagePlus className="w-5 h-5 text-gray-300" />}
                    </div>
                    <Input value={p.name} onChange={(e) => updateProduct(i, { name: e.target.value })}
                      placeholder="Nom de l'objet" className="flex-1" data-testid={`product-name-${i}`} />
                    <input type="file" accept="image/*" hidden id={`product-img-${i}`} onChange={(e) => uploadProductImage(e, i)} data-testid={`product-image-input-${i}`} />
                    <Button type="button" variant="outline" size="sm" className="rounded-lg" onClick={() => document.getElementById(`product-img-${i}`).click()} data-testid={`product-upload-${i}`}>
                      <ImagePlus className="w-4 h-4" />
                    </Button>
                    <button type="button" onClick={() => removeProduct(i)} className="text-red-500 hover:text-red-700" data-testid={`remove-product-${i}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <Input value={p.url} onChange={(e) => updateProduct(i, { url: e.target.value })}
                    placeholder="https://objetsdouceur.com/produit/..." data-testid={`product-url-${i}`} />
                  <Textarea value={p.desc} onChange={(e) => updateProduct(i, { desc: e.target.value })}
                    placeholder="Courte description" rows={2} data-testid={`product-desc-${i}`} />
                </div>
              ))}
            </div>

            <label className="flex items-center gap-3 pt-1 cursor-pointer border-t mt-2 pt-4">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="game-active-switch" />
              <span className="text-sm text-gray-700">Actif cette semaine (visible par les patients)</span>
            </label>
            {PRINTABLE.includes(form.category) ? (
              <label className="flex items-center gap-3 cursor-pointer">
                <Switch checked={!!form.printable} onCheckedChange={(v) => setForm({ ...form, printable: v })} data-testid="game-printable-switch" />
                <span className="text-sm text-gray-700">Imprimable : les soignants et aidants peuvent télécharger une fiche papier avec le corrigé</span>
              </label>
            ) : (
              <p className="text-xs text-gray-400" data-testid="game-printable-unavailable">Ce type de jeu (son, vidéo ou animation) ne peut pas être imprimé.</p>
            )}
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-game-button">
              {saving ? "Enregistrement..." : editing ? "Enregistrer" : "Ajouter le jeu"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={pwdOpen} onOpenChange={setPwdOpen}>
        <DialogContent data-testid="password-dialog">
          <DialogHeader>
            <DialogTitle>Changer le mot de passe</DialogTitle>
            <DialogDescription>Choisissez un mot de passe solide (6 caractères minimum).</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Mot de passe actuel</Label>
              <Input type="password" value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })}
                className="mt-1" data-testid="current-password-input" />
            </div>
            <div>
              <Label>Nouveau mot de passe</Label>
              <Input type="password" value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })}
                className="mt-1" data-testid="new-password-input" />
            </div>
            <div>
              <Label>Confirmer le nouveau mot de passe</Label>
              <Input type="password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
                className="mt-1" data-testid="confirm-password-input" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={changePwd} disabled={pwdSaving} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-password-button">
              {pwdSaving ? "Enregistrement..." : "Mettre à jour"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
